/**
 * Status Page API Client
 *
 * Communicates with the status-service (/api/status/*).
 * Public GET endpoints require no authentication.
 * Admin POST/PATCH endpoints require Bearer JWT.
 *
 * Errors are surfaced as either:
 *   - `FeatureDisabledError` (from `_http`) when nginx returns 503
 *     `feature_disabled` for an intentionally-disabled service. Consumers
 *     should swallow this and render an empty/disabled state.
 *   - `StatusApiError` for any other 4xx/5xx, so call sites can branch on
 *     `.status` (e.g. 403 → permission UI).
 */

import { FeatureDisabledError } from './_http';
export { FeatureDisabledError } from './_http';

const BASE = '/api/status';

export type ComponentStatus =
  | 'operational'
  | 'degraded'
  | 'partial_outage'
  | 'major_outage'
  | 'maintenance';

export type IncidentSeverity = 'minor' | 'major' | 'critical';

export type IncidentStatus =
  | 'detected'
  | 'investigating'
  | 'identified'
  | 'monitoring'
  | 'resolved';

export type IncidentSource = 'monitoring_alert' | 'manual';

export type MaintenanceWindowStatus = 'scheduled' | 'active' | 'in_progress' | 'completed' | 'cancelled';

export interface StatusComponent {
  id: string;
  name: string;
  group: string;
  status: ComponentStatus;
  /** null = N/A (insufficient data) */
  uptime_percentage: number | null;
  monitoring_start_date: string;
  last_checked_at: string;
  description: string;
  display_order: number;
  uptime_period_days?: number;
  uptime_has_gaps?: boolean;
  uptime_gap_percentage?: number | null;
}

export interface ComponentStatusChange {
  component_id: string;
  new_status: ComponentStatus;
}

export interface IncidentUpdate {
  id: string;
  incident_id: string;
  status: IncidentStatus;
  message: string;
  affected_components: ComponentStatusChange[];
  author_id: string;
  created_at: string;
}

export interface StatusIncident {
  id: string;
  title: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  affected_components: string[];
  source: IncidentSource;
  monitoring_alert_id: string | null;
  postmortem_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface IncidentWithDetails extends StatusIncident {
  updates: IncidentUpdate[];
  affected_component_names: string[];
}

export interface MaintenanceWindow {
  id: string;
  title: string;
  /** Human-readable message / description */
  message: string | null;
  /** @deprecated use message */
  description?: string;
  affected_components: string[];
  /** ISO8601 UTC start timestamp (API field) */
  start_at: string;
  /** ISO8601 UTC end timestamp (API field) */
  end_at: string;
  /** @deprecated use start_at */
  scheduled_start?: string;
  /** @deprecated use end_at */
  scheduled_end?: string;
  actual_start: string | null;
  actual_end: string | null;
  /** API status: 'active' maps to DB 'in_progress' */
  status: MaintenanceWindowStatus;
  auto_status_update: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateMaintenanceWindowParams {
  title: string;
  message?: string;
  start_at: string;
  end_at: string;
  affected_components?: string[];
}

export interface UpdateMaintenanceWindowParams {
  title?: string;
  message?: string;
  start_at?: string;
  end_at?: string;
  affected_components?: string[];
  status?: 'cancelled';
  updated_at?: string;
}

export interface ListMaintenanceWindowsResponse {
  maintenance_windows: MaintenanceWindow[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListMaintenanceWindowsParams {
  status?: string;
  component_id?: string;
  limit?: number;
  offset?: number;
}

export type ActionItemStatus = 'pending' | 'in_progress' | 'completed';

export interface ActionItem {
  description: string;
  assignee?: string;
  due_date?: string;
  status: ActionItemStatus;
}

export interface PostmortemReport {
  id: string;
  incident_id: string;
  title: string;
  summary: string;
  timeline: Array<{ timestamp: string; status: string; message: string }>;
  root_cause: string;
  impact: string;
  resolution: string;
  action_items: ActionItem[];
  lessons_learned: string[];
  created_at: string;
  published_at: string | null;
}

// ── Response shapes ───────────────────────────────────────────────────────────

export interface ListComponentsResponse {
  components: StatusComponent[];
}

export interface ListIncidentsResponse {
  incidents: IncidentWithDetails[];
  total: number;
  page: number;
  has_more: boolean;
}

export interface GetIncidentResponse {
  incident: IncidentWithDetails;
}

export interface CreateIncidentResponse {
  incident: StatusIncident;
  initial_update: IncidentUpdate;
  notifications_queued: number;
}

export interface PostUpdateResponse {
  update: IncidentUpdate;
  incident: StatusIncident;
  notifications_queued: number;
}

// ── Subscription types ────────────────────────────────────────────────────────

export type SubscriptionChannel = 'email' | 'webhook' | 'rss';

export interface StatusSubscription {
  id: string;
  email: string | null;
  webhook_url: string | null;
  channel: SubscriptionChannel;
  component_ids: string[] | null;
  severity_filter: IncidentSeverity[] | null;
  confirmed: boolean;
  unsubscribe_token: string;
  cleanup_failed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateEmailSubscriptionParams {
  email: string;
  component_ids?: string[] | null;
  severity_filter?: IncidentSeverity[] | null;
}

export interface CreateWebhookSubscriptionParams {
  webhook_url: string;
  component_ids?: string[] | null;
  severity_filter?: IncidentSeverity[] | null;
}

export interface CreateSubscriptionResponse {
  subscription: StatusSubscription;
  confirmation_required: boolean;
  message: string;
}

// ── SSE event types ───────────────────────────────────────────────────────────

export type SseEventType =
  | 'incident_created'
  | 'incident_updated'
  | 'component_status'
  | 'maintenance_started'
  | 'maintenance_completed';

export interface SseEvent {
  event_id: number;
  type: SseEventType;
  data: Record<string, unknown>;
  timestamp: string;
}

// ── Error ─────────────────────────────────────────────────────────────────────

export class StatusApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'StatusApiError';
  }
}

// ── Fetch helper ──────────────────────────────────────────────────────────────

async function apiFetch<T>(
  url: string,
  init?: RequestInit & { token?: string },
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> ?? {}),
  };

  if (init?.token) {
    headers['Authorization'] = `Bearer ${init.token}`;
  }

  const res = await fetch(url, { ...init, headers });

  // Detect intentionally-disabled-feature 503s before raising StatusApiError so
  // consumers can `catch (err) { if (err instanceof FeatureDisabledError) ... }`.
  if (res.status === 503) {
    type FeatureDisabledBody = { error?: string; feature?: string };
    let body: FeatureDisabledBody | null = null;
    try {
      body = (await res.clone().json()) as FeatureDisabledBody;
    } catch {
      try {
        body = JSON.parse(await res.clone().text()) as FeatureDisabledBody;
      } catch {
        body = null;
      }
    }
    if (body?.error === 'feature_disabled') {
      throw new FeatureDisabledError(body.feature ?? 'unknown');
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new StatusApiError(
      res.status,
      (body as { error?: string }).error ?? res.statusText,
    );
  }

  return res.json() as Promise<T>;
}

// ── API client ────────────────────────────────────────────────────────────────

export const statusApi = {
  // ── Components ─────────────────────────────────────────────────────────────

  /** Fetch all monitored components with current status and uptime. */
  async getComponents(): Promise<ListComponentsResponse> {
    return apiFetch<ListComponentsResponse>(`${BASE}/components`);
  },

  // ── Incidents ──────────────────────────────────────────────────────────────

  /** Fetch paginated list of incidents with optional filtering. */
  async getIncidents(
    params: {
      status?: string;
      severity?: string;
      active?: boolean;
      component_id?: string;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<ListIncidentsResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.severity) qs.set('severity', params.severity);
    if (params.active !== undefined) qs.set('active', String(params.active));
    if (params.component_id) qs.set('component_id', params.component_id);
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    return apiFetch<ListIncidentsResponse>(`${BASE}/incidents${query}`);
  },

  /** Fetch a single incident with full update timeline. */
  async getIncident(id: string): Promise<GetIncidentResponse> {
    return apiFetch<GetIncidentResponse>(`${BASE}/incidents/${id}`);
  },

  /** Create a new incident (admin). */
  async createIncident(
    params: {
      title: string;
      severity: IncidentSeverity;
      affected_component_ids: string[];
      initial_message: string;
      update_component_status?: boolean;
      new_component_status?: ComponentStatus;
      metadata?: Record<string, unknown>;
    },
    token: string,
  ): Promise<CreateIncidentResponse> {
    return apiFetch<CreateIncidentResponse>(`${BASE}/incidents`, {
      method: 'POST',
      body: JSON.stringify(params),
      token,
    });
  },

  /** Post a status update to an incident (admin). */
  async postIncidentUpdate(
    incidentId: string,
    params: {
      status: IncidentStatus;
      message: string;
      component_status_changes?: ComponentStatusChange[];
    },
    token: string,
  ): Promise<PostUpdateResponse> {
    return apiFetch<PostUpdateResponse>(
      `${BASE}/incidents/${incidentId}/updates`,
      { method: 'POST', body: JSON.stringify(params), token },
    );
  },

  // ── Postmortems ────────────────────────────────────────────────────────────

  async getPostmortem(
    incidentId: string,
    token?: string,
  ): Promise<{ postmortem: PostmortemReport }> {
    return apiFetch<{ postmortem: PostmortemReport }>(
      `${BASE}/incidents/${incidentId}/postmortem`,
      token ? { token } : undefined,
    );
  },

  async createPostmortem(
    incidentId: string,
    params: Partial<Omit<PostmortemReport, 'id' | 'incident_id' | 'created_at' | 'published_at'>>,
    token: string,
  ): Promise<{ postmortem: PostmortemReport }> {
    return apiFetch<{ postmortem: PostmortemReport }>(
      `${BASE}/incidents/${incidentId}/postmortem`,
      { method: 'POST', body: JSON.stringify(params), token },
    );
  },

  async updatePostmortem(
    incidentId: string,
    params: Partial<Omit<PostmortemReport, 'id' | 'incident_id' | 'created_at' | 'published_at'>>,
    token: string,
  ): Promise<{ postmortem: PostmortemReport }> {
    return apiFetch<{ postmortem: PostmortemReport }>(
      `${BASE}/incidents/${incidentId}/postmortem`,
      { method: 'PATCH', body: JSON.stringify(params), token },
    );
  },

  async publishPostmortem(
    incidentId: string,
    token: string,
  ): Promise<{ postmortem: PostmortemReport }> {
    return apiFetch<{ postmortem: PostmortemReport }>(
      `${BASE}/incidents/${incidentId}/postmortem/publish`,
      { method: 'POST', token },
    );
  },

  // ── Maintenance ────────────────────────────────────────────────────────────

  /** List maintenance windows with optional filtering. */
  async getMaintenanceWindows(
    params: ListMaintenanceWindowsParams = {},
  ): Promise<ListMaintenanceWindowsResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.component_id) qs.set('component_id', params.component_id);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return apiFetch<ListMaintenanceWindowsResponse>(`${BASE}/maintenance${query}`);
  },

  /** Create a new maintenance window (admin). */
  async createMaintenanceWindow(
    params: CreateMaintenanceWindowParams,
    token: string,
  ): Promise<{ maintenance_window: MaintenanceWindow }> {
    return apiFetch<{ maintenance_window: MaintenanceWindow }>(`${BASE}/maintenance`, {
      method: 'POST',
      body: JSON.stringify(params),
      token,
    });
  },

  /** Update or cancel a maintenance window (admin). */
  async updateMaintenanceWindow(
    id: string,
    params: UpdateMaintenanceWindowParams,
    token: string,
  ): Promise<{ maintenance_window: MaintenanceWindow }> {
    return apiFetch<{ maintenance_window: MaintenanceWindow }>(`${BASE}/maintenance/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
      token,
    });
  },

  // ── SSE ────────────────────────────────────────────────────────────────────

  /**
   * Connect to the SSE stream for real-time status updates.
   * Returns a cleanup function to close the connection.
   */
  connectToStream(
    onEvent: (type: SseEventType, data: Record<string, unknown>, eventId: number) => void,
    onError?: (err: Event) => void,
    lastEventId?: string,
  ): () => void {
    const url = lastEventId
      ? `${BASE}/stream`
      : `${BASE}/stream`;

    const es = lastEventId
      ? (() => {
          // EventSource doesn't support Last-Event-ID header directly on construction,
          // it's sent automatically on reconnect. We use a custom fetch for initial connect.
          return new EventSource(url);
        })()
      : new EventSource(url);

    const handleTypedEvent = (type: SseEventType) => (e: Event) => {
      const msg = e as MessageEvent;
      try {
        const parsed = JSON.parse(msg.data as string) as {
          event_id: number;
          data: Record<string, unknown>;
        };
        onEvent(type, parsed.data ?? parsed, parsed.event_id ?? 0);
      } catch {
        // ignore malformed events
      }
    };

    const eventTypes: SseEventType[] = [
      'incident_created',
      'incident_updated',
      'component_status',
      'maintenance_started',
      'maintenance_completed',
    ];

    for (const type of eventTypes) {
      es.addEventListener(type, handleTypedEvent(type));
    }

    if (onError) {
      es.addEventListener('error', onError);
    }

    return () => es.close();
  },
};

// ── Subscription API client ────────────────────────────────────────────────────

export const subscriptionApi = {
  /** Create an email subscription (public, no auth required). */
  async createEmailSubscription(
    params: CreateEmailSubscriptionParams,
  ): Promise<CreateSubscriptionResponse> {
    return apiFetch<CreateSubscriptionResponse>(`${BASE}/subscriptions`, {
      method: 'POST',
      body: JSON.stringify({ channel: 'email', ...params }),
    });
  },

  /** Create a webhook subscription. Server will test the URL. */
  async createWebhookSubscription(
    params: CreateWebhookSubscriptionParams,
  ): Promise<CreateSubscriptionResponse> {
    return apiFetch<CreateSubscriptionResponse>(`${BASE}/subscriptions`, {
      method: 'POST',
      body: JSON.stringify({ channel: 'webhook', ...params }),
    });
  },

  /** Confirm an email subscription via token (from confirmation email link). */
  async confirmSubscription(
    token: string,
  ): Promise<{ message: string; subscription: StatusSubscription }> {
    return apiFetch<{ message: string; subscription: StatusSubscription }>(
      `${BASE}/subscriptions/confirm/${token}`,
    );
  },

  /** Unsubscribe using the unsubscribe token (from notification email footer). */
  async unsubscribe(
    token: string,
  ): Promise<{ message: string }> {
    return apiFetch<{ message: string }>(
      `${BASE}/subscriptions/unsubscribe/${token}`,
    );
  },

  /** Request a verification email to view preferences (step 1 of 2). */
  async requestVerificationEmail(email: string): Promise<{ message: string }> {
    return apiFetch<{ message: string }>(
      `${BASE}/subscriptions/${encodeURIComponent(email)}/verify-request`,
      { method: 'POST' },
    );
  },

  /** Get subscriptions for an email (requires verification token or admin JWT). */
  async getSubscriptions(
    email: string,
    params: { verificationToken?: string; token?: string } = {},
  ): Promise<{ subscriptions: StatusSubscription[] }> {
    const qs = new URLSearchParams();
    if (params.verificationToken) qs.set('verification_token', params.verificationToken);
    const query = qs.toString() ? `?${qs}` : '';
    return apiFetch<{ subscriptions: StatusSubscription[] }>(
      `${BASE}/subscriptions/${encodeURIComponent(email)}${query}`,
      params.token ? { token: params.token } : undefined,
    );
  },

  /** Update subscription preferences (component_ids, severity_filter). */
  async updateSubscription(
    id: string,
    params: {
      component_ids?: string[] | null;
      severity_filter?: IncidentSeverity[] | null;
    },
    auth: { unsubscribeToken?: string; token?: string } = {},
  ): Promise<StatusSubscription> {
    const qs = new URLSearchParams();
    if (auth.unsubscribeToken) qs.set('unsubscribe_token', auth.unsubscribeToken);
    const query = qs.toString() ? `?${qs}` : '';
    return apiFetch<StatusSubscription>(
      `${BASE}/subscriptions/${id}${query}`,
      { method: 'PATCH', body: JSON.stringify(params), ...(auth.token ? { token: auth.token } : {}) },
    );
  },

  /** Delete a subscription. */
  async deleteSubscription(
    id: string,
    auth: { unsubscribeToken?: string; token?: string } = {},
  ): Promise<void> {
    const qs = new URLSearchParams();
    if (auth.unsubscribeToken) qs.set('unsubscribe_token', auth.unsubscribeToken);
    const query = qs.toString() ? `?${qs}` : '';
    await apiFetch<void>(
      `${BASE}/subscriptions/${id}${query}`,
      { method: 'DELETE', ...(auth.token ? { token: auth.token } : {}) },
    );
  },

  /** Get the RSS feed URL. */
  getRssFeedUrl(): string {
    return `${BASE}/feed.rss`;
  },
};
